/**
 * The visitor's choice about non-essential trackers (Microsoft Clarity, Google Analytics, the Meta
 * Pixel, the product events). Plausible is cookieless and runs without a choice.
 *
 * The choice lives in first-party `localStorage` under one key. music24.com and the web app at
 * music24.com/app share the origin, so both read and write the same key. The website keeps a twin
 * of this module (`music24-website/app/src/components/consent/trackingConsent.ts`): keep the key,
 * the values and the event names equal in both files.
 *
 * Every storage access sits in a try/catch: a browser that denies storage (Safari with blocked
 * site data, a private window, an in-app browser) reads "no choice" and the page still works.
 */

export const TRACKING_CONSENT_KEY = 'm24TrackingConsent';
/** Sent on `window` after the choice changes in this tab. */
export const TRACKING_CONSENT_CHANGED_EVENT = 'm24:tracking-consent-changed';
/** Sent on `window` to show the consent bar again ("Cookie settings"). */
export const OPEN_TRACKING_CONSENT_EVENT = 'm24:open-tracking-consent';

export type TrackingConsent = 'granted' | 'denied';

// The choice of this page view when the storage refuses to keep it.
let memoryConsent: TrackingConsent | null = null;

const readStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.localStorage ?? null;
  } catch {
    return null;
  }
};

/** The stored choice, or `null` when the visitor did not choose yet (or storage is blocked). */
export const getTrackingConsent = (): TrackingConsent | null => {
  const storage = readStorage();
  if (storage === null) {
    return memoryConsent;
  }
  try {
    const value = storage.getItem(TRACKING_CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : memoryConsent;
  } catch {
    return memoryConsent;
  }
};

export const hasTrackingConsent = (): boolean => getTrackingConsent() === 'granted';

/** Stores the choice and tells the listeners in this tab. Other tabs get a `storage` event. */
export const setTrackingConsent = (consent: TrackingConsent): void => {
  // A blocked, full or read-only storage keeps the choice for this page view only.
  memoryConsent = consent;
  const storage = readStorage();
  if (storage !== null) {
    try {
      storage.setItem(TRACKING_CONSENT_KEY, consent);
      memoryConsent = null;
    } catch {
      // Keep the in-memory choice.
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<TrackingConsent>(TRACKING_CONSENT_CHANGED_EVENT, { detail: consent }));
  }
};

export const openTrackingConsent = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_TRACKING_CONSENT_EVENT));
  }
};

// First-party cookies the trackers set: Google Analytics, the Meta Pixel, Microsoft Clarity and our
// anonymous id, plus `ajs_*` from Segment (removed 2026-10-01; old visits can still carry it).
// Reject removes them, so a withdrawn consent leaves nothing behind.
const TRACKER_COOKIE = /^(_ga|_gid|_gat|_gcl_|_fbp|_fbc|_clck|_clsk|ajs_|anonymousIdentifierV1)/;

/** Deletes the tracker cookies on this host and on each parent domain. */
export const clearTrackerCookies = (): void => {
  if (typeof document === 'undefined') {
    return;
  }
  try {
    const names = document.cookie
      .split(';')
      .map((part) => part.split('=')[0]?.trim() ?? '')
      .filter((name) => TRACKER_COOKIE.test(name));
    const labels = window.location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < labels.length - 1; i += 1) {
      domains.push(`; domain=.${labels.slice(i).join('.')}`);
    }
    names.forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      });
    });
  } catch {
    // A browser that blocks cookies has none to delete.
  }
};
